import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import ConfirmDialog from "./ConfirmDialog";
import {
  downloadSelection,
  getErrorMessage,
  loadJson,
  readApiError,
  responseFileName,
  saveBlob,
  TabProps,
  toCountMap,
} from "./lib";
import { compressScrapPhoto, formatBytes, formatTimestamp, makeThumbnail, SCRAP_MAX_BYTES } from "./scrap-photos";
import {
  ScrapWeightContract,
  ScrapWeightContractDetail,
  ScrapWeightImage,
} from "./scrap-weight";
import { CameraScanProvider, ScanField } from "./scan";

const SIZE_LIMIT_KB = Math.round(SCRAP_MAX_BYTES / 1024);

/**
 * Scrap Weight upload — the same idea as Scrap Photo upload, minus the
 * Item/SN layer: a contract is scanned in, filled straight with photos, and
 * sealed. Its own contract list, entirely separate from Scrap Photo
 * upload's — a contract number can be reused across the two tabs.
 */
export default function ScrapWeightUploadTab({
  isActive,
  selectedDate,
  rangeFrom,
  rangeTo,
  refreshToken,
  onCounts,
  onSynced,
}: TabProps) {
  const [contracts, setContracts] = useState<ScrapWeightContract[]>([]);
  const [isAdding, setIsAdding] = useState(false);
  const [newContractNumber, setNewContractNumber] = useState("");
  const [openContractId, setOpenContractId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ScrapWeightContractDetail | null>(null);
  const [selectedImageIds, setSelectedImageIds] = useState<Set<number>>(new Set());
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFileProgress, setUploadFileProgress] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState("");
  const [isDeletingSelection, setIsDeletingSelection] = useState(false);
  const [confirmDeleteSelection, setConfirmDeleteSelection] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [confirmDeleteContract, setConfirmDeleteContract] =
    useState<ScrapWeightContract | null>(null);
  const [isDeletingContract, setIsDeletingContract] = useState(false);
  const [reopeningId, setReopeningId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const formRef = useRef<HTMLFormElement>(null);

  const loadCounts = useCallback(async () => {
    const data = await loadJson<{ counts: { date: string; count: number }[] }>(
      `/api/scrap-weight-contracts?from=${rangeFrom}&to=${rangeTo}`,
      "A szerződések betöltése sikertelen.",
    );
    onCounts(toCountMap(data.counts));
  }, [rangeFrom, rangeTo, onCounts]);

  const loadContracts = useCallback(async (date: string) => {
    const data = await loadJson<{ contracts: ScrapWeightContract[] }>(
      `/api/scrap-weight-contracts?date=${date}`,
      "A szerződések betöltése sikertelen.",
    );
    setContracts(data.contracts);
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([loadCounts(), loadContracts(selectedDate)])
      .then(() => {
        if (!cancelled) onSynced(true);
      })
      .catch((loadError) => {
        if (!cancelled) {
          setError(getErrorMessage(loadError, "A szerződések betöltése sikertelen."));
          onSynced(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [loadCounts, loadContracts, selectedDate, refreshToken, onSynced]);

  useEffect(() => {
    setOpenContractId(null);
    setDetail(null);
    setSelectedImageIds(new Set());
    setIsAdding(false);
    setNewContractNumber("");
  }, [selectedDate]);

  const openContract = async (id: number) => {
    setError("");
    setSelectedImageIds(new Set());
    setOpenContractId(id);
    try {
      const data = await loadJson<{ contract: ScrapWeightContractDetail }>(
        `/api/scrap-weight-contract?id=${id}`,
        "A szerződés betöltése sikertelen.",
      );
      setDetail(data.contract);
    } catch (loadError) {
      setError(getErrorMessage(loadError, "A szerződés betöltése sikertelen."));
      setOpenContractId(null);
    }
  };

  const closePanel = () => {
    setOpenContractId(null);
    setDetail(null);
    setSelectedImageIds(new Set());
  };

  const addContract = async () => {
    const contractNumber = newContractNumber.trim();
    if (!contractNumber) return;

    setIsAdding(true);
    setError("");
    try {
      const response = await fetch("/api/scrap-weight-contracts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contractNumber, recordDate: selectedDate }),
      });
      if (!response.ok) {
        setError(await readApiError(response, "A szerződés létrehozása sikertelen."));
        return;
      }

      const data = (await response.json()) as { contract: ScrapWeightContractDetail };
      setNewContractNumber("");
      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
      setOpenContractId(data.contract.id);
      setDetail(data.contract);
      setSelectedImageIds(new Set());
    } catch (addError) {
      setError(getErrorMessage(addError, "A szerződés létrehozása sikertelen."));
    } finally {
      setIsAdding(false);
    }
  };

  const addPhotos = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = "";
    if (files.length === 0 || !openContractId) return;

    setIsUploading(true);
    setUploadFileProgress(`1/${files.length}`);
    setError("");
    try {
      // One request per photo: a batch that breaks off halfway still leaves
      // everything uploaded so far, and a single huge combined request full
      // of raw, uncompressed originals was hitting Cloudflare's own request
      // size and time limits on anything more than a handful of photos.
      const uploadedImages: ScrapWeightImage[] = [];
      for (let index = 0; index < files.length; index += 1) {
        setUploadFileProgress(`${index + 1}/${files.length}`);

        const form = new FormData();
        form.append("photos", files[index], files[index].name);

        const response = await fetch(`/api/scrap-weight-photos?id=${openContractId}`, {
          method: "POST",
          body: form,
        });
        if (!response.ok) {
          setError(
            `${await readApiError(response, "A fotó feltöltése sikertelen.")} (${
              uploadedImages.length
            }/${files.length} már feltöltve)`,
          );
          return;
        }

        const data = (await response.json()) as { images: ScrapWeightImage[] };
        uploadedImages.push(...data.images);
        setDetail((current) =>
          current ? { ...current, images: [...current.images, ...data.images] } : current,
        );
      }

      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
    } catch (uploadError) {
      setError(getErrorMessage(uploadError, "A fotó feltöltése sikertelen."));
    } finally {
      setIsUploading(false);
      setUploadFileProgress("");
    }
  };

  const toggleSelect = (imageId: number) => {
    setSelectedImageIds((current) => {
      const next = new Set(current);
      if (next.has(imageId)) next.delete(imageId);
      else next.add(imageId);
      return next;
    });
  };

  const selectAll = () => {
    if (!detail) return;
    setSelectedImageIds(new Set(detail.images.map((image) => image.id)));
  };

  const clearSelection = () => setSelectedImageIds(new Set());

  const deleteSelected = async () => {
    if (selectedImageIds.size === 0 || !detail) return;
    setIsDeletingSelection(true);
    setError("");
    try {
      const ids = Array.from(selectedImageIds);
      const response = await fetch("/api/scrap-weight-images", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!response.ok) {
        setError(await readApiError(response, "A törlés sikertelen."));
        return;
      }

      setDetail((current) =>
        current
          ? { ...current, images: current.images.filter((image) => !selectedImageIds.has(image.id)) }
          : current,
      );
      setSelectedImageIds(new Set());
      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "A törlés sikertelen."));
    } finally {
      setIsDeletingSelection(false);
      setConfirmDeleteSelection(false);
    }
  };

  const saveAndClose = async () => {
    if (!openContractId || !detail) return;
    setIsSaving(true);
    setError("");
    try {
      // Every photo is squeezed under the size limit before the contract is
      // sealed — except photos a previous SAVE already compressed, which are
      // skipped entirely so a re-opened contract doesn't re-download and
      // re-upload bytes that are already done. The original is fetched back,
      // compressed in the browser, and the compressed copy replaces the
      // original in place — which is also what discards the original on
      // the server.
      const images = detail.images.filter((image) => !image.isCompressed);

      for (let index = 0; index < images.length; index += 1) {
        const image = images[index];
        setSaveProgress(`${index + 1}/${images.length}`);

        const original = await fetch(`/api/scrap-weight-image?id=${image.id}`, {
          cache: "no-store",
        });
        if (!original.ok) {
          setError(await readApiError(original, "A kép betöltése sikertelen."));
          return;
        }

        const originalFile = new File([await original.blob()], image.fileName, {
          type: image.contentType,
        });
        const compressed = await compressScrapPhoto(originalFile);
        if (compressed.error) {
          setError(compressed.error);
          return;
        }
        const thumbnail = await makeThumbnail(compressed.file);

        const form = new FormData();
        form.append("photo", compressed.file, compressed.file.name);
        if (thumbnail) form.append("thumbnail", thumbnail, thumbnail.name);
        const replaceResponse = await fetch(`/api/scrap-weight-image?id=${image.id}`, {
          method: "PUT",
          body: form,
        });
        if (!replaceResponse.ok) {
          setError(await readApiError(replaceResponse, "A kép tömörítése sikertelen."));
          return;
        }
      }

      const response = await fetch(`/api/scrap-weight-contract?id=${openContractId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "done" }),
      });
      if (!response.ok) {
        setError(await readApiError(response, "A mentés sikertelen."));
        return;
      }

      closePanel();
      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
    } catch (saveError) {
      setError(getErrorMessage(saveError, "A mentés sikertelen."));
    } finally {
      setIsSaving(false);
      setSaveProgress("");
    }
  };

  const downloadZip = async (contract: ScrapWeightContract) => {
    setDownloadingId(contract.id);
    setError("");
    try {
      await downloadSelection(
        `/api/scrap-weight-contracts/export?id=${contract.id}`,
        [],
        `${contract.contractNumber}.zip`,
      );
    } catch (downloadError) {
      setError(getErrorMessage(downloadError, "A letöltés sikertelen."));
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadFolder = async (contract: ScrapWeightContract) => {
    setDownloadingId(contract.id);
    setError("");
    try {
      const data = await loadJson<{ contract: ScrapWeightContractDetail }>(
        `/api/scrap-weight-contract?id=${contract.id}`,
        "A szerződés betöltése sikertelen.",
      );

      for (const image of data.contract.images) {
        const response = await fetch(
          `/api/scrap-weight-image?id=${image.id}&download=1`,
          { cache: "no-store" },
        );
        if (!response.ok) {
          setError(await readApiError(response, "A kép letöltése sikertelen."));
          continue;
        }
        const fileName = responseFileName(response) ?? image.fileName;
        saveBlob(await response.blob(), fileName);
      }
    } catch (downloadError) {
      setError(getErrorMessage(downloadError, "A letöltés sikertelen."));
    } finally {
      setDownloadingId(null);
    }
  };

  const deleteContract = async (contract: ScrapWeightContract) => {
    setIsDeletingContract(true);
    setError("");
    try {
      const response = await fetch(`/api/scrap-weight-contract?id=${contract.id}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        setError(await readApiError(response, "A szerződés törlése sikertelen."));
        return;
      }

      if (openContractId === contract.id) closePanel();
      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "A szerződés törlése sikertelen."));
    } finally {
      setIsDeletingContract(false);
      setConfirmDeleteContract(null);
    }
  };

  const reopenContract = async (contract: ScrapWeightContract) => {
    setReopeningId(contract.id);
    setError("");
    try {
      const response = await fetch(`/api/scrap-weight-contract?id=${contract.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "open" }),
      });
      if (!response.ok) {
        setError(await readApiError(response, "A szerződés megnyitása sikertelen."));
        return;
      }

      await Promise.all([loadCounts(), loadContracts(selectedDate)]);
      await openContract(contract.id);
    } catch (reopenError) {
      setError(getErrorMessage(reopenError, "A szerződés megnyitása sikertelen."));
    } finally {
      setReopeningId(null);
    }
  };

  const onCameraValue = useCallback((field: string, value: string) => {
    if (field === "newContractNumber") setNewContractNumber(value);
  }, []);

  if (!isActive) return null;

  return (
    <CameraScanProvider formRef={formRef} onValue={onCameraValue}>
      <form
        ref={formRef}
        className="scrap-tab"
        onSubmit={(event) => event.preventDefault()}
      >
        {error && <p className="status-message is-error">{error}</p>}

        {!isAdding && openContractId === null && (
          <button
            className="create-task-button"
            type="button"
            onClick={() => setIsAdding(true)}
          >
            + ADD CONTRACT NUMBER
          </button>
        )}

        {isAdding && (
          <div className="scrap-add-row">
            <ScanField
              label="Contract number"
              name="newContractNumber"
              value={newContractNumber}
              onValue={(_field, value) => setNewContractNumber(value)}
            />
            <button
              className="save-button"
              type="button"
              disabled={!newContractNumber.trim()}
              onClick={() => void addContract()}
            >
              ADD
            </button>
            <button
              className="modify-button"
              type="button"
              onClick={() => {
                setIsAdding(false);
                setNewContractNumber("");
              }}
            >
              CANCEL
            </button>
          </div>
        )}

        <div className="scrap-contract-list">
          {contracts.length === 0 && (
            <p className="empty-state">No scrap contracts yet.</p>
          )}

          {contracts.map((contract) => {
            const isOpenPanel = openContractId === contract.id;
            const isDone = contract.status === "done";

            return (
              <div
                key={contract.id}
                className={`scrap-contract-card ${isDone ? "is-done" : "is-open"}`}
              >
                <button
                  type="button"
                  className="scrap-contract-header"
                  onClick={() => {
                    if (isOpenPanel) closePanel();
                    else if (!isDone) void openContract(contract.id);
                  }}
                  disabled={isDone}
                >
                  <div className="scrap-contract-header-top">
                    <span className="scrap-contract-number">{contract.contractNumber}</span>
                    <span className="scrap-contract-status">
                      {isDone ? "DONE" : "OPEN"}
                    </span>
                  </div>
                  <div className="scrap-contract-header-bottom">
                    <span className="scrap-contract-count">
                      {contract.photoCount} photo{contract.photoCount === 1 ? "" : "s"}
                    </span>
                    {isDone && contract.completedAt && (
                      <time className="scrap-contract-timestamp">
                        {formatTimestamp(contract.completedAt)}
                      </time>
                    )}
                  </div>
                </button>

                {isDone && !isOpenPanel && (
                  <div className="scrap-download-actions">
                    <button
                      className="download-button"
                      type="button"
                      disabled={downloadingId === contract.id}
                      onClick={() => void downloadFolder(contract)}
                    >
                      FOLDER
                    </button>
                    <button
                      className="download-button"
                      type="button"
                      disabled={downloadingId === contract.id}
                      onClick={() => void downloadZip(contract)}
                    >
                      ZIP
                    </button>
                    <button
                      className="modify-button"
                      type="button"
                      disabled={reopeningId === contract.id}
                      onClick={() => void reopenContract(contract)}
                    >
                      {reopeningId === contract.id ? "OPENING…" : "MODIFY"}
                    </button>
                    <button
                      className="delete-button"
                      type="button"
                      onClick={() => setConfirmDeleteContract(contract)}
                    >
                      DELETE
                    </button>
                  </div>
                )}

                {!isDone && !isOpenPanel && (
                  <div className="scrap-download-actions">
                    <button
                      className="delete-button"
                      type="button"
                      onClick={() => setConfirmDeleteContract(contract)}
                    >
                      DELETE
                    </button>
                  </div>
                )}

                {isOpenPanel && detail && (
                  <div className="scrap-contract-panel">
                    <div className="scrap-panel-top-actions">
                      <button
                        className="save-button"
                        type="button"
                        disabled={isSaving || detail.images.length === 0}
                        onClick={() => void saveAndClose()}
                      >
                        {isSaving ? `COMPRESSING ${saveProgress}…` : "SAVE"}
                      </button>
                    </div>

                    <p className="photo-warning">
                      Photos are uploaded at full size. On SAVE, every photo is compressed
                      to JPEG, at most {SIZE_LIMIT_KB} KB — the original is discarded.
                    </p>

                    {detail.images.length > 0 && (
                      <div className="selection-toolbar">
                        <button type="button" className="modify-button" onClick={selectAll}>
                          SELECT ALL ({detail.images.length})
                        </button>
                        {selectedImageIds.size > 0 && (
                          <>
                            <button
                              type="button"
                              className="modify-button"
                              onClick={clearSelection}
                            >
                              CLEAR
                            </button>
                            <button
                              type="button"
                              className="delete-button"
                              disabled={isDeletingSelection}
                              onClick={() => setConfirmDeleteSelection(true)}
                            >
                              DELETE ({selectedImageIds.size})
                            </button>
                          </>
                        )}
                      </div>
                    )}

                    <div className="scrap-photo-grid">
                      {detail.images.map((image) => (
                        <label key={image.id} className="scrap-photo-tile">
                          <input
                            type="checkbox"
                            checked={selectedImageIds.has(image.id)}
                            onChange={() => toggleSelect(image.id)}
                          />
                          {image.isCompressed ? (
                            <img
                              className="scrap-photo-thumb"
                              src={`/api/scrap-weight-image?id=${image.id}&thumb=1`}
                              alt=""
                              loading="lazy"
                            />
                          ) : (
                            <span
                              className="scrap-photo-thumb scrap-photo-thumb-placeholder"
                              title="Preview loads after SAVE — the original is not downloaded early to save mobile data."
                            >
                              <svg aria-hidden="true" viewBox="0 0 24 24">
                                <path d="M4 7h3l1.5-2h7L17 7h3a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z" />
                                <circle cx="12" cy="13" r="3.2" />
                              </svg>
                            </span>
                          )}
                          <span className="scrap-photo-name">{image.fileName}</span>
                          <span className="scrap-photo-size">{formatBytes(image.byteSize)}</span>
                        </label>
                      ))}
                    </div>

                    <label className="photo-input">
                      <span className={isUploading ? "photo-name" : "photo-placeholder"}>
                        {isUploading ? `uploading ${uploadFileProgress}…` : "+ add photos"}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        disabled={isUploading}
                        onChange={(event) => void addPhotos(event)}
                      />
                    </label>

                    <div className="scrap-panel-actions">
                      <button
                        className="delete-button"
                        type="button"
                        onClick={() => setConfirmDeleteContract(contract)}
                      >
                        DELETE
                      </button>
                      <button className="modify-button" type="button" onClick={closePanel}>
                        BACK
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {confirmDeleteSelection && (
          <ConfirmDialog
            title="Delete selected photos?"
            message={`${selectedImageIds.size} photo${
              selectedImageIds.size === 1 ? "" : "s"
            } will be removed.`}
            confirmLabel="DELETE"
            isBusy={isDeletingSelection}
            onCancel={() => setConfirmDeleteSelection(false)}
            onConfirm={() => void deleteSelected()}
          />
        )}

        {confirmDeleteContract && (
          <ConfirmDialog
            title="Delete this contract?"
            message={`"${confirmDeleteContract.contractNumber}" and all ${
              confirmDeleteContract.photoCount
            } of its photos will be permanently removed.`}
            confirmLabel="DELETE"
            isBusy={isDeletingContract}
            onCancel={() => setConfirmDeleteContract(null)}
            onConfirm={() => void deleteContract(confirmDeleteContract)}
          />
        )}
      </form>
    </CameraScanProvider>
  );
}
